import {
  Box,
  HStack,
  SimpleGrid,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { ConnectedMcpAppRO } from "@intentsify/dto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "components/Button";
import { useConfirm } from "components/ConfirmDialog/useConfirm";
import { Loader } from "components/Loader";
import { DateTime } from "luxon";
import { MdDevices } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { HttpStatus } from "types/HttpStatus";
import { handleApiError } from "utils";
import { ConnectedMcpUsage } from "./ConnectedMcpUsage";
import {
  listConnectedMcpApps,
  revokeConnectedMcpApp,
} from "./ConnectedMcpApps.requests";

const QUERY_KEY = ["connectedMcpApps"];

const scopeDescription = (scope: string): string => {
  if (scope === "mcp:read") return "Read Intentsify MCP data";
  if (scope === "mcp:write") return "Change Intentsify MCP data";
  return scope;
};

const registrationDescription = (app: ConnectedMcpAppRO): string => {
  if (app.registration === "metadata-document" && app.verifiedDomain) {
    return `Verified app · ${app.verifiedDomain}`;
  }
  if (app.registration === "dynamic") {
    return "Unverified app · Dynamic registration";
  }
  return "Pre-registered app";
};

const responseStatus = (error: unknown): unknown =>
  (error as { response?: { statusCode?: unknown } } | undefined)?.response
    ?.statusCode;

const ConnectedMcpApps = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const apps = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listConnectedMcpApps,
    retry: false,
    useErrorBoundary: false,
    onError: (error) => {
      if (responseStatus(error) !== HttpStatus.FORBIDDEN) {
        handleApiError(error, navigate, toast);
      }
    },
  });

  const revoke = useMutation({
    mutationFn: revokeConnectedMcpApp,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: "MCP application access revoked.", status: "success" });
    },
    onError: () => {
      toast({
        title: "Failed to revoke MCP application access.",
        status: "error",
      });
    },
  });

  const requestRevoke = (app: ConnectedMcpAppRO) => {
    void confirm({
      title: `Revoke ${app.clientName}?`,
      description:
        "This application will no longer be able to access Intentsify MCP data.",
      confirmText: "Confirm revoke",
    }).then((confirmed) => {
      if (confirmed) revoke.mutate(app.grantId);
    });
  };

  const isIneligible = responseStatus(apps.error) === HttpStatus.FORBIDDEN;

  return (
    <Box>
      <HStack mt={4}>
        <MdDevices />
        <Text mt={1} mb={1} fontWeight="bold" fontSize="2xl">
          Connected MCP applications
        </Text>
      </HStack>
      <Text color="gray.500" fontSize="sm" mb={3} ml={6}>
        Review and revoke applications connected to your Intentsify account.
      </Text>

      {apps.isLoading && <Loader />}
      {isIneligible && (
        <Text ml={6} color="gray.500">
          MCP access is not enabled for your company.
        </Text>
      )}
      {apps.isError && !isIneligible && (
        <Text ml={6} color="gray.500">
          Connected applications could not be loaded. Please try again.
        </Text>
      )}
      {apps.data?.length === 0 && (
        <Text ml={6} color="gray.500">
          No connected MCP applications.
        </Text>
      )}
      {apps.data && apps.data.length > 0 && (
        <SimpleGrid columns={1} spacingY={4} ml={4} mt={4} maxWidth="1100px">
          {apps.data.map((app) => (
            <Stack key={app.grantId} p={4} rounded="lg" shadow="md" spacing={2}>
              <HStack justify="space-between" align="start">
                <Box>
                  <Text fontWeight="bold" fontSize="md">
                    {app.clientName}
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    {registrationDescription(app)}
                  </Text>
                </Box>
                <Button
                  variant="warning"
                  isLoading={
                    revoke.isLoading && revoke.variables === app.grantId
                  }
                  onClick={() => requestRevoke(app)}
                >
                  Revoke {app.clientName}
                </Button>
              </HStack>
              <Box fontSize="sm">
                {app.scopes.map((scope) => (
                  <Text key={scope}>{scopeDescription(scope)}</Text>
                ))}
                <Text overflowWrap="anywhere">{app.resource}</Text>
                <Text color="gray.500">
                  Approved{" "}
                  {DateTime.fromISO(app.approvedAt).toLocaleString(
                    DateTime.DATE_MED,
                  )}
                </Text>
              </Box>
            </Stack>
          ))}
        </SimpleGrid>
      )}
      {apps.data && apps.data.length > 0 && <ConnectedMcpUsage />}
    </Box>
  );
};

export { ConnectedMcpApps };
